<template>
  <div
    @mousemove="handleUserActivity"
    @click="handleUserActivity"
    :style="wrapperStyle"
  >
    <video
      ref="video"
      :poster="previewImageLink"
      :title="title"
      autoplay
      playsinline
      muted
      @pause="handlePause"
      @play="handlePlay"
      @ended="handlePause"
      @keyup="changeSpeed"
      :style="videoStyle"
    ></video>

    <div v-show="showControls" :style="controlBarStyle"></div>

    <!-- Play/Pause -->
    <button @click.stop="togglePlay" v-show="showControls" :style="buttonStyle">
      <svg
        v-if="!isPlaying"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        width="24"
        height="24"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        width="24"
        height="24"
      >
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
      </svg>
    </button>

    <!-- Mute/Unmute -->
    <button
      @click.stop="toggleMute"
      v-show="showControls"
      :style="muteButtonStyle"
    >
      <svg
        v-if="isMutedState"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        width="24"
        height="24"
      >
        <path
          d="M16.5 12L19 9.5 17.59 8.09 15 10.59 12.41 8 11 9.41 13.59 12 11 14.59 12.41 16 15 13.41 17.59 16 19 14.59 16.5 12zM5 9v6h4l5 5V4l-5 5H5z"
        />
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        width="24"
        height="24"
      >
        <path d="M5 9v6h4l5 5V4l-5 5H5z" />
      </svg>
    </button>

    <input
      v-if="!isIOS"
      type="range"
      min="0"
      max="1"
      step="0.01"
      v-model.number="volume"
      @input="updateVolume"
      v-show="showControls"
      :style="sliderStyle"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
// @ts-nocheck
import { ref, onMounted, computed } from "vue";
import Hls from "hls.js";

const props = defineProps({
  previewImageLink: String,
  link: { type: String, required: true },
  progress: { type: Number, default: 0 },
  title: { type: String, default: "" },
  isMuted: { type: Boolean, default: true },
});

const emit = defineEmits(["pause"]);

const video = ref(null);
const isPlaying = ref(false);
const showControls = ref(true);
let hideTimeout = null;
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

onMounted(() => {
  prepareVideoPlayer();
  startHideTimer();
});

function prepareVideoPlayer() {
  if (!video.value || !props.link) return;

  if (Hls.isSupported()) {
    const hls = new Hls();
    hls.loadSource(props.link);
    hls.attachMedia(video.value);
  } else if (video.value.canPlayType("application/vnd.apple.mpegurl")) {
    video.value.src = props.link;
  }

  video.value.muted = props.isMuted;
  video.value.currentTime = props.progress;
}

function togglePlay() {
  if (!video.value) return;

  if (video.value.paused) {
    // ⚡ Jump to live edge
    try {
      const buffered = video.value.buffered;
      const lastRange = buffered.length ? buffered.end(buffered.length - 1) : 0;
      video.value.currentTime = lastRange;
    } catch (e) {
      console.warn("Could not seek to live edge:", e);
    }

    video.value.play();
  } else {
    video.value.pause();
  }
}

function handlePause() {
  isPlaying.value = false;
  emit("pause", (video.value && video.value.currentTime) || 0);
}

function handlePlay() {
  isPlaying.value = true;
}

function changeSpeed(e) {
  if (!video.value) return;
  if (e.key === "w") video.value.playbackRate += 0.25;
  if (e.key === "s") video.value.playbackRate -= 0.25;
}

// Auto-hide logic
function handleUserActivity() {
  showControls.value = true;
  resetHideTimer();
}

function startHideTimer() {
  hideTimeout = setTimeout(() => {
    showControls.value = false;
  }, 3000);
}

function resetHideTimer() {
  clearTimeout(hideTimeout);
  startHideTimer();
}

const volume = ref(1); // default full volume
const isMutedState = ref(true); // UI toggle state

function toggleMute() {
  if (!video.value) return;

  isMutedState.value = !isMutedState.value;
  video.value.muted = isMutedState.value;
}

function updateVolume() {
  if (!video.value) return;

  video.value.volume = volume.value;
  // Automatically unmute when changing volume
  if (video.value.muted && volume.value > 0) {
    video.value.muted = false;
    isMutedState.value = false;
  }
}

// Inline styles
const wrapperStyle = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  display: "inline-block",
  background: "black",
};

const videoStyle = {
  width: "100%",
  height: "auto",
  display: "block",
  background: "black",
};

const controlBarStyle = computed(() => ({
  position: "absolute",
  bottom: "0",
  left: "0",
  width: "100%",
  height: "60px",
  backgroundColor: "rgba(0, 0, 0, 1)",
  zIndex: 5,
  opacity: showControls.value ? 1 : 0,
  transition: "opacity 0.3s ease",
  pointerEvents: "none",
}));

const buttonStyle = computed(() => ({
  ...commonButtonStyle(),
  left: "20px",
}));

const muteButtonStyle = computed(() => ({
  ...commonButtonStyle(),
  left: "100px",
}));

const sliderStyle = computed(() => ({
  position: "absolute",
  bottom: "17px",
  left: "200px",
  zIndex: 10,
  width: "100px",
  opacity: showControls.value ? 1 : 0,
  transition: "opacity 0.3s ease",
  pointerEvents: showControls.value ? "auto" : "none",
}));

function commonButtonStyle() {
  return {
    position: "absolute",
    bottom: "3px",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "white",
    border: "none",
    padding: "10px 15px",
    fontSize: "16px",
    cursor: "pointer",
    zIndex: 10,
    opacity: showControls.value ? 1 : 0,
    transition: "opacity 0.3s ease",
    pointerEvents: showControls.value ? "auto" : "none",
  };
}
</script>
